import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import KanbanCard from './KanbanCard';

const BACKLOG_COLOR = '#64748b';

const BacklogColumn = ({ items, loading }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const { setNodeRef, isOver } = useDroppable({
    id: 'column-backlog',
    data: { columnId: 'backlog' },
  });

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase().trim();
    return items.filter((item) => {
      const firmName =
        item.job?.contract?.firm?.firm_name || item.job?.firm_short_name || '';
      const jobTitle = item.job?.job_title || '';
      return `${firmName} ${jobTitle}`.toLowerCase().includes(q);
    });
  }, [items, search]);

  return (
    <Box
      ref={setNodeRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: 300,
        minWidth: 300,
        maxWidth: 300,
        bgcolor: isOver ? 'rgba(100, 116, 139, 0.06)' : '#f1f5f9',
        borderRadius: '12px',
        border: '2px dashed',
        borderColor: isOver ? '#3b82f6' : '#cbd5e1',
        flexShrink: 0,
        // Board container'in tum yuksekligini al ki cards bolumu kendi
        // icinde scroll edebilsin.
        height: '100%',
        transition: 'all 0.2s ease',
        boxShadow: isOver ? '0 0 0 2px rgba(59,130,246,0.15)' : 'none',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1.25,
          bgcolor: '#e2e8f0',
          borderBottom: '1px solid #cbd5e1',
        }}
      >
        <ViewListIcon sx={{ fontSize: '1.1rem', color: BACKLOG_COLOR }} />
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '0.82rem',
            flex: 1,
            color: '#334155',
          }}
        >
          {t('reportManagement.backlog.title', { defaultValue: 'Bekleyen Raporlar' })}
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 22,
            height: 22,
            borderRadius: '6px',
            bgcolor: '#cbd5e1',
            color: '#475569',
            fontSize: '0.72rem',
            fontWeight: 700,
            px: 0.5,
          }}
        >
          {filteredItems.length}
        </Box>
      </Box>

      {/* Search */}
      <Box sx={{ px: 1, py: 1, bgcolor: '#e2e8f0' }}>
        <TextField
          size="small"
          fullWidth
          placeholder={t('reportManagement.backlog.search', { defaultValue: 'Ara...' })}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: '1rem', color: '#94a3b8' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: '#fff',
              fontSize: '0.8rem',
              '& fieldset': { borderColor: '#cbd5e1' },
            },
            '& .MuiInputBase-input': { py: 0.75 },
          }}
        />
      </Box>

      {/* Cards */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1,
          py: 1,
          // Parent yuksekligi sabit oldugu icin maxHeight'a gerek yok;
          // minHeight: 0 olmazsa flex child scroll'u dogru hesaplayamiyor.
          minHeight: 0,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 2 },
          '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ color: '#94a3b8' }} />
          </Box>
        )}

        {!loading && (
          <SortableContext
            items={filteredItems.map((i) => i.id)}
            strategy={verticalListSortingStrategy}
          >
            {filteredItems.map((item) => (
              <KanbanCard
                key={item.id}
                item={item}
                columnColor={BACKLOG_COLOR}
                isRoot={false}
              />
            ))}
          </SortableContext>
        )}

        {!loading && filteredItems.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 4,
              color: '#94a3b8',
            }}
          >
            <ViewListIcon sx={{ fontSize: '1.5rem', color: '#cbd5e1', mb: 0.5 }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, textAlign: 'center' }}>
              {search
                ? t('reportManagement.backlog.noResults', { defaultValue: 'Sonuç bulunamadı' })
                : t('reportManagement.backlog.empty', { defaultValue: 'Tüm raporlar akışta' })}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BacklogColumn;
